import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import stakingAbi from "./StakingV4ABI.json";

const STAKING_ADDRESS = "0xC3A14dcc88D77Eb65C74aDE295185267FbB94930";
const TOKEN_ADDRESS = "0xD3caeCab1663ccF26ced842cFa73BB1bB3A05029";
const CHAIN_ID = "0x1"; // Ethereum Mainnet

const erc20Abi = [
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)"
];

function App() {
  const [account, setAccount] = useState(null);
  const [stakingContract, setStakingContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);

  const [stakeAmount, setStakeAmount] = useState("");
  const [lockDays, setLockDays] = useState("");
  const [unstakeAmount, setUnstakeAmount] = useState("");

  const [tokenBalance, setTokenBalance] = useState("0");
  const [allowance, setAllowance] = useState("0");

  const [board, setBoard] = useState([]);
  const [burnAddress, setBurnAddress] = useState("");

  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Mainnet Staking Board";
  }, []);

  // ---------------- CONNECT WALLET ----------------
  const connectWallet = async () => {
    try {
      if (!window.ethereum) throw new Error("MetaMask not installed");

      await window.ethereum.request({ method: "eth_requestAccounts" });
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: CHAIN_ID }]
      });

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      const stakingCtr = new ethers.Contract(STAKING_ADDRESS, stakingAbi, signer);
      const tokenCtr = new ethers.Contract(TOKEN_ADDRESS, erc20Abi, signer);

      setAccount(address);
      setStakingContract(stakingCtr);
      setTokenContract(tokenCtr);

      await loadUserData(stakingCtr, tokenCtr, address);

    } catch (err) {
      setError(err.message);
    }
  };

  // ---------------- LOAD USER + BOARD ----------------
  const loadUserData = async (stakingCtr, tokenCtr, user) => {
    const balanceRaw = await tokenCtr.balanceOf(user);
    const allowanceRaw = await tokenCtr.allowance(user, STAKING_ADDRESS);

    setTokenBalance(ethers.formatUnits(balanceRaw, 18));
    setAllowance(ethers.formatUnits(allowanceRaw, 18));

    const boardAddresses = await stakingCtr.getBoard();
    setBoard(boardAddresses);

    const burnCtr = await stakingCtr.burnContract();
    setBurnAddress(burnCtr);
  };

  // ---------------- APPROVE ----------------
  const handleApprove = async () => {
    try {
      const parsed = ethers.parseUnits(stakeAmount, 18);
      const tx = await tokenContract.approve(STAKING_ADDRESS, parsed);
      await tx.wait();
      await loadUserData(stakingContract, tokenContract, account);
    } catch (err) {
      setError(err.reason || err.message);
    }
  };

  // ---------------- STAKE ----------------
  const handleStake = async () => {
    try {
      const parsed = ethers.parseUnits(stakeAmount, 18);
      const tx = await stakingContract.stake(parsed, Number(lockDays));
      await tx.wait();
      await loadUserData(stakingContract, tokenContract, account);
    } catch (err) {
      setError(err.reason || err.message);
    }
  };

  // ---------------- UNSTAKE ----------------
  const handleUnstake = async () => {
    try {
      const parsed = ethers.parseUnits(unstakeAmount, 18);
      const tx = await stakingContract.unstake(parsed);
      await tx.wait();
      await loadUserData(stakingContract, tokenContract, account);
    } catch (err) {
      setError(err.reason || err.message);
    }
  };

  return (
    <div style={styles.container}>
      <h1>Mainnet Staking Board</h1>

      {!account && (
        <button style={styles.button} onClick={connectWallet}>
          Connect Wallet
        </button>
      )}

      {account && (
        <>
          <div style={styles.panel}>
            <p><strong>Connected:</strong> {account}</p>
            <p><strong>Token Balance:</strong> {tokenBalance}</p>
            <p><strong>Allowance:</strong> {allowance}</p>
            <p><strong>Burn Contract:</strong> {burnAddress}</p>

            <div style={styles.row}>
              <div style={styles.box}>
                <h3>Stake</h3>
                <input
                  style={styles.input}
                  placeholder="Amount"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                />
                <input
                  style={styles.input}
                  placeholder="Lock Days"
                  value={lockDays}
                  onChange={(e) => setLockDays(e.target.value)}
                />
                <button style={styles.button} onClick={handleApprove}>
                  Approve
                </button>
                <button style={styles.button} onClick={handleStake}>
                  Stake
                </button>
              </div>

              <div style={styles.box}>
                <h3>Unstake</h3>
                <input
                  style={styles.input}
                  placeholder="Amount"
                  value={unstakeAmount}
                  onChange={(e) => setUnstakeAmount(e.target.value)}
                />
                <button style={styles.button} onClick={handleUnstake}>
                  Unstake
                </button>
              </div>
            </div>
          </div>

          <h2>Top 7 Board Members</h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.cell}>Position</th>
                <th style={styles.cell}>Address</th>
              </tr>
            </thead>
            <tbody>
              {board.map((addr, i) => (
                <tr key={i}>
                  <td style={styles.cell}>{i + 1}</td>
                  <td style={styles.cell}>{addr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {error && <div style={{ color: "red" }}>{error}</div>}
    </div>
  );
}

// ---------------- STYLES ----------------
const styles = {
  container: {
    padding: 40,
    background: "#0f172a",
    color: "white",
    minHeight: "100vh"
  },
  panel: {
    background: "#334155",
    padding: 20,
    borderRadius: 8,
    marginBottom: 40
  },
  row: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap"
  },
  box: {
    background: "#475569",
    padding: 20,
    borderRadius: 8,
    minWidth: 260
  },
  input: {
    width: "100%",
    marginBottom: 10,
    padding: 6
  },
  button: {
    padding: "8px 16px",
    marginRight: 8,
    cursor: "pointer"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "center",
    marginTop: 20
  },
  cell: {
    padding: 12
  }
};

export default App;